import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Scroll } from '@phosphor-icons/react';
import { Link } from '../../../../../components/Link';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { Msg } from '../../../../../i18n-review/client/Msg';

export interface CollectiveAgreementNoticeProps {
  collectiveAgreementId?: string | null;
  collectiveAgreementName?: string | null;
  collectiveAgreementMatchType?: 'explicit' | 'inferred' | 'none' | null;
  collectiveAgreementReasoning?: string | null;
  jobLocale: string;
}

/**
 * Only two of the three match outcomes are ever shown — 'none' (or missing id) renders
 * nothing, since "we couldn't figure it out" isn't useful information on a job ad.
 * 'explicit' (employer stated it) and 'inferred' (best guess) get deliberately different
 * copy: explicit needs no hedging, inferred always carries the disclaimer + a link to the
 * full register so a reader can check it themselves — see the design discussion this
 * implements (job-level matching, not company-level, because Finnish collective agreements
 * commonly split by employment category within one industry).
 */
export function CollectiveAgreementNotice({
  collectiveAgreementId,
  collectiveAgreementName,
  collectiveAgreementMatchType,
  collectiveAgreementReasoning,
  jobLocale,
}: CollectiveAgreementNoticeProps): React.ReactElement | null {
  if (!collectiveAgreementId || !collectiveAgreementName) return null;
  if (collectiveAgreementMatchType !== 'explicit' && collectiveAgreementMatchType !== 'inferred') return null;

  const finlexHref = `https://www.finlex.fi/en/authorities/collective-agreements/${collectiveAgreementId}`;
  const registerHref = jobLocale === 'en' ? '/collective-agreements' : `/${jobLocale}/collective-agreements`;
  const name = fixMojibake(collectiveAgreementName);

  return (
    <div className="flex items-start gap-2 rounded-lg bg-[#F8F5F0] dark:bg-[#241708] px-3 py-2.5 text-[12.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882]">
      <Scroll className="h-4 w-4 flex-none mt-0.5 text-[#8C8474]" weight="duotone" />
      <div>
        {collectiveAgreementMatchType === 'explicit' ? (
          <Msg
            id="jobDetailPageV2.collectiveAgreement.explicit"
            defaultMessage="Employer states this job follows the {agreementName} collective agreement."
            description="Job detail page notice when the employer explicitly named their collective agreement in the ad"
            values={{
              agreementName: (
                <a href={finlexHref} target="_blank" rel="noopener" className="font-semibold underline decoration-dotted underline-offset-2 transition-opacity hover:opacity-70">
                  {name}
                </a>
              ),
            }}
          />
        ) : (
          <>
            <Msg
              id="jobDetailPageV2.collectiveAgreement.inferred"
              defaultMessage="Best guess: {agreementName} collective agreement — inferred from this employer's industry and the role, could be a different one."
              description="Job detail page disclaimer when the collective agreement is an inferred best guess, not stated by the employer"
              values={{
                agreementName: (
                  <a href={finlexHref} target="_blank" rel="noopener" className="font-semibold underline decoration-dotted underline-offset-2 transition-opacity hover:opacity-70">
                    {name}
                  </a>
                ),
              }}
            />
            {collectiveAgreementReasoning ? (
              <div className="mt-1 italic text-[#8C8474]">{fixMojibake(collectiveAgreementReasoning)}</div>
            ) : null}
            <Link href={registerHref} locale={jobLocale} className="mt-1 inline-block underline decoration-dotted underline-offset-2 text-inherit transition-opacity hover:opacity-70">
              <Msg
                id="jobDetailPageV2.collectiveAgreement.browseAll"
                defaultMessage="Browse all Finnish collective agreements"
                description="Link from the inferred collective agreement disclaimer to the full public register page"
              />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
